/**
 * Centralized URL builders for entity detail pages.
 * Single source of truth for /artists, /tours, /concerts, /venues links.
 *
 * Always prefer the database `slug`. Falls back to UUID for transitional
 * safety (detail pages still redirect UUID URLs to canonical slug URLs).
 */

export const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const isUUID = (v: string | null | undefined): boolean =>
  !!v && UUID_REGEX.test(v);

/** Slugify a name client-side. Mirrors public.slugify in the database. */
export const toSlug = (s: string | null | undefined): string =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/**
 * Extract a UUID from the end of a route param.
 * Supports both raw UUIDs and the legacy <slug>-<uuid> shape produced
 * by older getArtistLink() output.
 */
export const extractUUID = (param: string | null | undefined): string | null => {
  if (!param) return null;
  if (UUID_REGEX.test(param)) return param;
  const tail = param.slice(-36);
  if (UUID_REGEX.test(tail)) return tail;
  return null;
};

const ns = (kind: "artists" | "tours" | "concerts" | "venues") => `/${kind}`;

interface EntityRef {
  slug?: string | null;
  id?: string | null;
  name?: string | null;
}

const buildUrl = (kind: "artists" | "tours" | "concerts" | "venues", e: EntityRef): string => {
  if (e.slug) return `${ns(kind)}/${e.slug}`;
  if (e.id && isUUID(e.id)) return `${ns(kind)}/${e.id}`;
  // Preserve Ticketmaster-sourced ids (e.g. "tm-<eventId>-tour") so the
  // detail page can resolve via TM lookup instead of slugifying the name.
  if (e.id && kind === "tours" && /^tm-/.test(e.id)) return `${ns(kind)}/${e.id}`;
  if (e.name) return `${ns(kind)}/${toSlug(e.name)}`;
  return ns(kind);
};

export const artistUrl = (e: EntityRef): string => buildUrl("artists", e);
export const tourUrl = (e: EntityRef): string => buildUrl("tours", e);
export const concertUrl = (e: EntityRef): string => buildUrl("concerts", e);

/**
 * Venue URL — slug-only canonical form. When `slug` is missing, derives a
 * stable slug from name+city+country via slugify (matches DB convention).
 * VenueDetail will resolve the slug against the venues table; if that
 * lookup misses it falls back to its own name-based search.
 */
export const venueUrl = (
  e: EntityRef & { city?: string | null; state?: string | null; country?: string | null },
): string => {
  if (e.slug) return `${ns("venues")}/${e.slug}`;
  if (e.id && isUUID(e.id)) return `${ns("venues")}/${e.id}`;
  if (!e.name) return ns("venues");
  // Compose a slug similar to the DB trigger output (name [+ city/state]).
  const locality = e.city || e.state || "";
  const composite = locality ? `${e.name} ${locality}` : e.name;
  return `${ns("venues")}/${toSlug(composite)}`;
};
